import type { HTMLAttributes, ReactNode } from 'react';

import { nineSliceStyle } from './NineSlice.js';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  variant?: 'surface' | 'parchment' | 'inset' | 'framed';
}

const VARIANT_CLASSES: Record<NonNullable<CardProps['variant']>, string> = {
  surface: 'rounded-lg border border-border bg-surface shadow-2',
  parchment: 'parchment-texture rounded-lg border border-border bg-parchment shadow-2',
  inset: 'rounded-lg border border-border bg-inset shadow-inset',
  // `framed` swaps the 1px border for the shared brass 9-slice panel
  // (docs/UI_GUIDELINES.md §1, §13: ornament frames large regions, not every
  // control) — reserved for hero regions, applied via inline `border-image`.
  framed: 'parchment-texture rounded-lg bg-parchment shadow-3',
};

/** A simple elevated content container (TECH_STACK §16). */
export function Card({ children, className = '', variant = 'surface', style, ...rest }: CardProps) {
  const framedStyle =
    variant === 'framed' ? nineSliceStyle('uiFramePanelParchment', 8, false, 3) : undefined;
  return (
    <div
      className={`p-4 tablet:p-6 ${VARIANT_CLASSES[variant]} ${className}`}
      style={{ ...framedStyle, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}
